// Shared ExcelJS photo embedding: fits a remote image inside one cell (or a merged span of rows), centered.
import { loadImageAsDataUrl, getImageDimensions } from "./exportImageUtils";

export const EMU_PER_PIXEL = 9525;
const IMAGE_PADDING_RATIO = 0.06;
export const ptToPx = (pt) => pt * (96 / 72);
export const colWidthToPx = (chars) => chars * 7 + 5;

export function fitImageToCell(naturalWidth, naturalHeight, rowHeightPt, cellWidthPx) {
  const cellHeightPx = ptToPx(rowHeightPt);
  const drawableWidth = cellWidthPx * (1 - 2 * IMAGE_PADDING_RATIO);
  const drawableHeight = cellHeightPx * (1 - 2 * IMAGE_PADDING_RATIO);
  const scale = Math.min(drawableWidth / naturalWidth, drawableHeight / naturalHeight);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  return { width, height, offsetXPx: (cellWidthPx - width) / 2, offsetYPx: (cellHeightPx - height) / 2 };
}

/**
 * @param {Object} o
 * @param {import("exceljs").Workbook} o.workbook
 * @param {import("exceljs").Worksheet} o.sheet
 * @param {string} o.photoUrl
 * @param {number} o.rowNum        1-based first row of the cell
 * @param {number} o.col           1-based column index
 * @param {number} o.rowHeightPt   height of ONE row in points
 * @param {number} o.span          number of rows the cell spans (merged), default 1
 * @param {number} o.colWidthChars Excel column width in characters
 */
export async function embedPhoto({ workbook, sheet, photoUrl, rowNum, col, rowHeightPt, span = 1, colWidthChars }) {
  if (!photoUrl) return false;
  const dataUrl = await loadImageAsDataUrl(photoUrl);
  if (!dataUrl) return false;
  const match = dataUrl.match(/^data:image\/(\w+);base64,(.*)$/);
  if (!match) return false;
  const ext = match[1] === "png" ? "png" : "jpeg";
  const dims = await getImageDimensions(dataUrl);
  const cellWidthPx = colWidthToPx(colWidthChars);
  const { width, height, offsetXPx, offsetYPx } = fitImageToCell(
    dims?.width || cellWidthPx,
    dims?.height || ptToPx(rowHeightPt),
    rowHeightPt * span,
    cellWidthPx,
  );
  const imageId = workbook.addImage({ base64: dataUrl, extension: ext });
  const tlColOff = Math.round(offsetXPx * EMU_PER_PIXEL);
  const singleRowHeightEmu = Math.round(ptToPx(rowHeightPt) * EMU_PER_PIXEL);
  const toRowAnchor = (offsetPx) => {
    const offsetEmu = Math.round(offsetPx * EMU_PER_PIXEL);
    const rowsAdvanced = Math.floor(offsetEmu / singleRowHeightEmu);
    return { nativeRow: rowNum - 1 + rowsAdvanced, nativeRowOff: offsetEmu - rowsAdvanced * singleRowHeightEmu };
  };
  const fromAnchor = toRowAnchor(offsetYPx);
  const toAnchor = toRowAnchor(offsetYPx + height);
  const nativeCol = col - 1;
  sheet.addImage(imageId, {
    tl: { nativeCol, nativeColOff: tlColOff, nativeRow: fromAnchor.nativeRow, nativeRowOff: fromAnchor.nativeRowOff },
    br: { nativeCol, nativeColOff: tlColOff + Math.round(width * EMU_PER_PIXEL), nativeRow: toAnchor.nativeRow, nativeRowOff: toAnchor.nativeRowOff },
  });
  return true;
}
